import { createMemo } from "solid-js";
import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@cashsync/shared";
import { AuthScene } from "@/components/auth/auth-scene";
import { useI18n } from "@/providers/i18n-provider";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Session check for `_auth` guard.
 *
 * Lives at MODULE scope (not inside `beforeLoad`) for two reasons: it is what
 * `unicorn/consistent-function-scoping` requires, and keeps the pair
 * `fetchSession` + `syncReply` readable as what it is — the request and its
 * side effect, deliberately decoupled from the capped `await`.
 */
const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });

type FetchSessionResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Syncs machine and cache with the server reply. Hooks into the request's
 * `.then()` and NOT the route's `await`: when the reply arrives late (after
 * the cap) this still runs and the `BootManager` takes the user off `/login`
 * if there turned out to be a session.
 */
const syncReply = (reply: FetchSessionResult) => {
  const { data: replyData, error: replyError } = reply;
  // Synchronize the machine and the cache immediately.
  // This prevents the BootManager from seeing a "pending" or stale session
  // during the route transition, which causes redirection loops.
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

/**
 * Per-screen identity for CashSync's auth (#13 de-clone).
 *
 * CashSync no longer shares the generic glass-card-in-a-split frame. Its shell
 * is the `AuthScene` "financial cockpit": an edge-bleed brand panel (with the
 * live mini-dashboard) beside a flat, full-bleed ledger form. The brand panel
 * (lg+) is shown for the acquisition/login screens and dropped for the
 * transactional ones (reset/verify) that arrive from an email link.
 *
 * The eyebrow/headline/tagline live in the ledger header (which owns the page
 * h1), so each screen tunes the copy while the structure stays CashSync's own.
 */
type ScreenConfig = {
  showBrand: boolean;
  eyebrow?: MessageDescriptor;
  headline?: MessageDescriptor;
  tagline?: MessageDescriptor;
};

function screenConfigFor(pathname: string): ScreenConfig {
  if (pathname.includes("/register")) {
    return {
      showBrand: true,
      eyebrow: msg`New here`,
      headline: msg`Set up your money in minutes.`,
      tagline: msg`Create an account and start tracking accounts, budgets and transactions — synced, private and yours on every device.`,
    };
  }
  if (pathname.includes("/forgot-password")) {
    return {
      showBrand: true,
      eyebrow: msg`Account recovery`,
      headline: msg`Let's get you back to your books.`,
      tagline: msg`Tell us your email and we'll send a secure link to reset your password.`,
    };
  }
  if (pathname.includes("/reset-password")) {
    return {
      showBrand: false,
      eyebrow: msg`Account recovery`,
      headline: msg`Choose a new password.`,
      tagline: msg`Pick a strong password and you'll be back to your books in seconds.`,
    };
  }
  if (pathname.includes("/verify-email")) {
    return {
      showBrand: false,
      eyebrow: msg`Email verification`,
      headline: msg`Confirming your email.`,
    };
  }
  // Default: login.
  return {
    showBrand: true,
    eyebrow: msg`Welcome back`,
    headline: msg`Welcome back to CashSync.`,
    tagline: msg`Sign in to pick up your balances, budgets and transactions right where you left them.`,
  };
}

function AuthLayoutRoute() {
  const location = useLocation();
  const { t } = useI18n();
  appState.markAsReady();

  const config = createMemo(() => screenConfigFor(location().pathname));
  const tr = (m: MessageDescriptor | undefined) => (m ? t(m) : undefined);

  return (
    <AuthScene
      showBrand={config().showBrand}
      eyebrow={tr(config().eyebrow)}
      headline={tr(config().headline)}
      tagline={tr(config().tagline)}
    >
      <PageTransition routeKey={location().pathname}>
        <Outlet />
      </PageTransition>
    </AuthScene>
  );
}

/**
 * Redirects an already-authenticated guest away from the auth screens. Module scope
 * so the decision is testable without mounting the whole router.
 */
export const authBeforeLoad = async ({ search }: { search: Record<string, unknown> }) => {
  // Multi-account: `?mode=add` means the user is ADDING another account while
  // the current one stays signed in (Gmail-style). Do NOT bounce an
  // authenticated user away in that case — let them reach the login/register
  // form for the new account.
  const isAddMode = (search as { mode?: string }).mode === "add";

  // Fast path: check machine state before hitting the network.
  const snapshot = sessionActor.getSnapshot();
  if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
    if (snapshot.context.session && !isAddMode) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    return;
  }
  if (isAddMode) {
    // Adding an account: skip the network bounce-check entirely.
    return;
  }

  // Network check — only if machine has no session yet.
  // Use api.GET (not fetchApi) so we can inspect the status without throwing.
  //
  // P23/CC-60 · I1-bis: this `await` CANNOT be unlimited. A STALLED
  // `get-session` (held: neither a 401 nor a 5xx, which resolve fast) left
  // `beforeLoad` unresolved, the router without mounting the route and the
  // whole app BLANK — measured here: `#root.childElementCount === 0` at
  // 3/6/10/15/20 s on `/login`, also over the production bundle, while `/`
  // painted normally (that is why I1 closed green: the probe looked at the
  // root, which had a cap, and not at login, which did not).
  //
  // The request is NOT cancelled: it stays alive and syncs machine + cache
  // when it lands (`syncReply`). If there turned out to be a session, the
  // `BootManager` takes the user off the login screen: only a flicker is
  // paid, never an empty screen.
  const inFlight = fetchSession()
    .then(syncReply)
    // Network error — let user try to log in anyway.
    .catch(() => null);

  const timedOut = new Promise<null>((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, SESSION_CHECK_TIMEOUT_MS);
  });

  const result = await Promise.race([inFlight, timedOut]);
  if (!result) {
    // Cap expired or network error: the login IS painted. The late reply is
    // already hooked above and the BootManager redirects if it brings a session.
    return;
  }

  // Decide from the session the machine ACCEPTED, not from the raw reply: the
  // machine rejects a reply for a session that LOGOUT revoked, and a raw reply
  // would still redirect the guest back into a session they just left.
  // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
  if (sessionActor.getSnapshot().context.session) {
    // oxlint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
  }
  // 401/403, network error, null session, or a reply the machine refused — show login page.
};

// If already authenticated, redirect to the app root.
export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  beforeLoad: authBeforeLoad,
  component: AuthLayoutRoute,
});
